import React, { useState, useEffect } from 'react';
import apiService from '../services/ApiService';

const AppConfig = () => {
    const [activeTab, setActiveTab] = useState('app');
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const [app, contact, terms] = await Promise.all([
                apiService.get('/config/app'),
                apiService.get('/config/contact'),
                apiService.get('/config/terms')
            ]);
            setConfig({
                app: app.data,
                contact: contact.data,
                terms: terms.data
            });
        } catch (err) {
            console.error('Failed to fetch config:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        // Note: Backend update endpoints will be implemented next
        try {
            setTimeout(() => {
                alert('Configuration saved successfully! (Simulated)');
                setIsSaving(false);
            }, 1000);
        } catch (err) {
            alert('Failed to save configuration');
            setIsSaving(false);
        }
    };

    if (loading || !config) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
            <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
        </div>
    );

    return (
        <div className="config-page" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="page-header" style={{ marginBottom: '32px' }}>
                <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Platform Configuration</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Modify global application variables, legal clauses, and support channels.</p>
            </div>

            <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
                <div className="tabs" style={{
                    display: 'flex',
                    background: 'var(--bg-main)',
                    padding: '8px',
                    borderBottom: '1px solid var(--border-light)',
                    overflowX: 'auto',
                    scrollbarWidth: 'none'
                }}>
                    {[
                        { id: 'app', label: 'General', icon: 'layer-group' },
                        { id: 'contact', label: 'Support', icon: 'headset' },
                        { id: 'terms', label: 'Legal', icon: 'file-contract' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: 1, minWidth: '100px', padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px',
                                background: activeTab === tab.id ? 'white' : 'transparent',
                                color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-dim)',
                                boxShadow: activeTab === tab.id ? 'var(--shadow-sm)' : 'none',
                                transition: 'all 0.2s'
                            }}
                        >
                            <i className={`fas fa-${tab.icon}`} style={{ fontSize: '14px' }}></i>
                            <span className="m-hide">{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div style={{ padding: '40px' }} className="container-fluid">
                    <form onSubmit={handleSave}>
                        {activeTab === 'app' && (
                            <div className="config-section" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div className="grid-responsive">
                                    <div className="col-6 col-md-12">
                                        <label style={labelStyle}>APPLICATION IDENTIFIER</label>
                                        <input type="text" defaultValue={config.app.appName} style={inputStyle} />
                                    </div>
                                    <div className="col-6 col-md-12">
                                        <label style={labelStyle}>VERSION CONTROL</label>
                                        <input type="text" defaultValue={config.app.version} style={inputStyle} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={labelStyle}>BRAND TAGLINE</label>
                                    <input type="text" defaultValue={config.app.tagline} style={inputStyle} />
                                </div>
                                <div className="form-group">
                                    <label style={labelStyle}>MAINTENANCE STATUS</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'var(--bg-main)', borderRadius: '16px', flexWrap: 'wrap' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--warning)', boxShadow: 'var(--shadow-sm)' }}>
                                            <i className="fas fa-tools"></i>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '200px' }}>
                                            <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-main)' }}>Platform Lock Mode</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontWeight: '600' }}>Prevent all user interaction for critical updates.</div>
                                        </div>
                                        <select defaultValue="false" style={{ ...inputStyle, width: 'auto', marginLeft: 'auto', padding: '8px 16px' }}>
                                            <option value="false">Live Mode</option>
                                            <option value="true">Offline Mode</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'contact' && (
                            <div className="config-section" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div className="grid-responsive">
                                    <div className="col-6 col-md-12">
                                        <label style={labelStyle}>SUPPORT EMAIL</label>
                                        <input type="email" defaultValue={config.contact.email} style={inputStyle} />
                                    </div>
                                    <div className="col-6 col-md-12">
                                        <label style={labelStyle}>HOTLINE NUMBER</label>
                                        <input type="text" defaultValue={config.contact.phone} style={inputStyle} />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={labelStyle}>WHATSAPP INTEGRATION HUB</label>
                                    <input type="text" defaultValue={config.contact.whatsapp} style={inputStyle} />
                                </div>
                                <div className="form-group">
                                    <label style={labelStyle}>OPERATIONAL HOURS</label>
                                    <input type="text" defaultValue={config.contact.businessHours} style={inputStyle} />
                                </div>
                            </div>
                        )}

                        {activeTab === 'terms' && (
                            <div className="config-section">
                                <div className="form-group">
                                    <label style={labelStyle}>GOVERNANCE & CLAUSES (MARKDOWN)</label>
                                    <textarea rows="15" defaultValue={config.terms.content} style={{ ...inputStyle, resize: 'none', lineHeight: '1.6', fontSize: '13px' }}></textarea>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '40px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
                            <button type="submit" className="btn btn-primary" style={{ padding: '14px 32px' }} disabled={isSaving}>
                                {isSaving ? <i className="fas fa-spinner fa-spin"></i> : (
                                    <>
                                        <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
                                        Apply Configurations
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const labelStyle = {
    display: 'block',
    fontSize: '10px',
    fontWeight: '800',
    color: 'var(--text-dim)',
    letterSpacing: '1px',
    marginBottom: '8px'
};

const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid var(--border-light)',
    fontSize: '14px',
    fontFamily: 'inherit',
    fontWeight: '600',
    background: '#fcfcfc',
    transition: 'all 0.2s',
    outline: 'none',
    boxSizing: 'border-box'
};

export default AppConfig;

